import { alpha } from '@mui/material';
import { palette } from '@client/theme/colors';
import { radii, theme } from '@client/theme/constants';
import { shadows } from '@client/theme/shadows';
import { typography } from '@client/theme/typography';
import type { ThemeOptions } from '@mui/material';

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    grey: true;
    secondary_dark: true;
    textPrimary: true;
    textSecondary: true;
  }

  interface ButtonPropsSizeOverrides {
    inline: true;
  }

  interface ButtonPropsVariantOverrides {
    menu: true;
  }
}

declare module '@mui/material/Fab' {
  interface FabPropsVariantOverrides {
    square: true;
  }
}

/*
  Merge these into the Button props to bypass typescript issues
  https://github.com/mui/material-ui/issues/32699
*/
const extraVariants: any = {
  containedWarning: {
    '&:hover': {
      backgroundColor: palette.warning.light,
    },

    '&:disabled': {
      backgroundColor: palette.warning.light,
    },
  },
  containedInfo: {
    backgroundColor: palette.grey[600],

    '&:hover': {
      backgroundColor: palette.grey[500],
    },

    '&:disabled': {
      backgroundColor: palette.grey[500],
    },
  },
  containedSuccess: {
    '&:hover': {
      backgroundColor: palette.success.light,
    },

    '&:disabled': {
      backgroundColor: palette.success.light,
    },
  },
  containedError: {
    '&:hover': {
      backgroundColor: palette.error.light,
    },

    '&:disabled': {
      backgroundColor: palette.error.light,
    },
  },
  outlinedWarning: {
    borderColor: palette.warning.dark,
    color: palette.warning.dark,

    '&:hover': {
      borderColor: palette.warning.light,
      color: palette.warning.light,
    },

    '&:disabled': {
      borderColor: palette.warning.light,
      color: palette.warning.light,
    },

    '&:focus, &:active': {
      color: palette.warning.dark,
    },
  },
  outlinedInfo: {
    borderColor: palette.grey[600],
    color: palette.grey[600],

    '&:hover': {
      borderColor: palette.grey[500],
      color: palette.grey[500],
    },

    '&:disabled': {
      borderColor: palette.grey[400],
      color: palette.grey[400],
    },

    '&:focus, &:active': {
      color: palette.grey[600],
    },
  },
  outlinedSuccess: {
    borderColor: palette.success.main,

    '&:hover': {
      borderColor: palette.success.light,
      color: palette.success.light,
    },

    '&:disabled': {
      borderColor: palette.success.light,
      color: palette.success.light,
    },

    '&:focus, &:active': {
      color: palette.success.main,
    },
  },
  outlinedError: {
    borderColor: palette.error.main,

    '&:hover': {
      borderColor: palette.error.light,
      color: palette.error.light,
    },

    '&:disabled': {
      borderColor: palette.error.light,
      color: palette.error.light,
    },

    '&:focus, &:active': {
      color: palette.error.main,
    },
  },
  /**
  * Button sizes apply to all button styles
  * Available sizes: small, medium (default), and large
  * The classes exist in mui but are placed in a separate object for ease of access
  */
  sizeSmall: {
    padding: theme.spacing(1 / 2, 1),
    height: 30,
    borderRadius: radii.small,
    fontSize: typography.caption.fontSize,
    lineHeight: typography.caption.lineHeight,
    fontWeight: 700,
  },
  sizeMedium: {
    padding: theme.spacing(1, 1 + 1 / 2),
    height: 40,
    borderRadius: radii.small,
    fontSize: typography.button.fontSize,
    lineHeight: typography.button.lineHeight,
    fontWeight: 600,
  },
  sizeLarge: {
    padding: theme.spacing(1 + 1 / 2, 2),
    height: 54,
    borderRadius: radii.small,
    fontSize: typography.subtitle1.fontSize,
    lineHeight: typography.subtitle1.lineHeight,
    fontWeight: 600,
  },
};

const Button: ThemeOptions['components'] = {
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true, // No more ripple on the whole application
      disableTouchRipple: true,
    },
  },
  MuiButton: {
    defaultProps: {
      type: 'button',
    },
    styleOverrides: {
      root: {
        boxShadow: 'none',

        [theme.breakpoints.down('md')]: {
          '&.mobileFullWidth': {
            ...extraVariants.sizeLarge,
            width: '100%',
          },
        },

        '&:hover': {
          boxShadow: shadows[4],
        },

        '&:focus': {
          boxShadow: 'none',
        },

        '& > svg:first-of-type:not(:only-child), & > span.material-icons:not(:only-child)': {
          marginRight: theme.spacing(1),
        },

        '& > svg:last-child:not(:only-child)': {
          marginLeft: theme.spacing(1),
        },

        '& > svg:only-child': {
          textAlign: 'center',
        },

        '& > svg': {
          verticalAlign: 'middle',
        },
      },
      contained: {
        color: palette.grey[50],

        '&:focus, &:active': {
          color: alpha(palette.grey[50], 0.8),
        },

        '&:disabled': {
          color: palette.grey[50],
        },

        '&:active': {
          boxShadow: shadows[2],
        },
      },
      containedPrimary: {
        '&:hover': {
          backgroundColor: palette.primary.light,
        },

        '&:disabled': {
          backgroundColor: palette.primary.light,
        },
      },
      containedSecondary: {
        '&:hover': {
          backgroundColor: palette.secondary.light,
        },

        '&:disabled': {
          backgroundColor: palette.secondary.light,
        },
      },
      outlined: {
        backgroundColor: `${palette.grey[50]} !important`,
        borderWidth: '1.5px !important',

        '&:hover': {
          borderWidth: '1.5px !important',
        },

        '&:active': {
          boxShadow: shadows[2],
        },
      },
      outlinedPrimary: {
        borderColor: palette.primary.main,

        '&:hover': {
          borderColor: palette.primary.light,
          color: palette.primary.light,
        },

        '&:disabled': {
          borderColor: palette.primary.light,
          color: palette.primary.light,
        },

        '&:focus, &:active': {
          color: palette.primary.main,
        },
      },
      outlinedSecondary: {
        borderColor: palette.secondary.main,

        '&:hover': {
          borderColor: palette.secondary.light,
          color: palette.secondary.light,
        },

        '&:disabled': {
          borderColor: palette.secondary.light,
          color: palette.secondary.light,
        },

        '&:focus, &:active': {
          color: palette.secondary.main,
        },
      },
      colorInherit: {
        color: typography.caption.color,

        '&$contained': {
          padding: '12px 16px',
          backgroundColor: palette.grey[400],
          boxShadow: 'none',

          '&:hover': {
            backgroundColor: palette.grey[500],
          },
        },
      },
      text: ({ ownerState }) => {
        const { color } = ownerState;

        let paletteColor;

        if (color === 'textPrimary' || color === 'inherit' || color === 'grey') {
          paletteColor = palette.text.primary;
        } else if (color === 'textSecondary') {
          paletteColor = palette.text.secondary;
        } else if (!color) {
          paletteColor = palette.primary.main;
        } else {
          paletteColor = palette[color].main;
        }

        return {
          textDecoration: `underline ${alpha(paletteColor, 0.4)}`,

          '&:hover': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            textDecoration: 'none',
          },
        };
      },
      ...extraVariants,
    },
    variants: [
      /**
       * Red Variants
       */
      {
        props: { variant: 'contained', color: 'error' },
        style: {
          color: palette.grey[50],
          backgroundColor: palette.error.main,

          '&:hover': {
            color: palette.grey[50],
            backgroundColor: palette.error.light,
          },
        },
      },
      {
        props: { variant: 'outlined', color: 'error' },
        style: {
          color: palette.error.main,
          backgroundColor: palette.grey[50],
          borderColor: palette.error.main,

          '&:hover': {
            color: palette.error.dark,
            backgroundColor: palette.grey[50],
            borderColor: palette.error.dark,
          },
        },
      },
      /**
       * Dark purple (home page) variants
       */
      {
        props: { variant: 'contained', color: 'secondary_dark' },
        style: {
          color: palette.grey[50],
          backgroundColor: palette.secondary.dark,

          '&:hover': {
            color: palette.grey[50],
            backgroundColor: palette.secondary.main,
          },
        },
      },
      {
        props: { variant: 'outlined', color: 'secondary_dark' },
        style: {
          color: palette.secondary.dark,
          backgroundColor: palette.grey[50],
          borderColor: palette.secondary.dark,

          '&:hover': {
            color: palette.secondary.main,
            backgroundColor: palette.grey[50],
            borderColor: palette.secondary.main,
          },
        },
      },
      {
        props: { variant: 'text', color: 'error' },
        style: {
          color: palette.error.main,

          '&:hover': {
            color: palette.error.main,
          },
        },
      },
      {
        props: { variant: 'text', color: 'textPrimary' },
        style: {
          color: palette.text.primary,

          '&:hover': {
            color: palette.text.primary,
          },
        },
      },
      {
        props: { variant: 'text', color: 'textSecondary' },
        style: {
          color: palette.text.secondary,

          '&:hover': {
            color: palette.text.secondary,
          },
        },
      },
      /**
       * Grey Variant
       */
      {
        props: { variant: 'contained', color: 'grey' },
        style: {
          color: palette.text.primary,
          backgroundColor: palette.grey[300],
          minWidth: 'auto',

          '&:hover': {
            color: palette.grey[50],
            backgroundColor: palette.primary.main,
          },
        },
      },
      /**
       * Inline button with 34px height
       */
      {
        props: { size: 'inline' },
        style: {
          height: 34,
        },
      },
      /**
       * Menu Links
       */
      {
        props: { variant: 'menu' },
        style: {
          fontWeight: 400,
        },
      },
      {
        props: { variant: 'menu', color: 'primary' },
        style: {
          color: palette.text.primary,

          '&:hover': {
            color: palette.primary.main,
            backgroundColor: palette.grey[50],
          },
        },
      },
    ],
  },
  MuiFab: {
    defaultProps: {
      color: 'inherit',
      size: 'medium',
      variant: 'extended',
    },
    styleOverrides: {
      root: {
        boxShadow: 'none',

        '&:hover': {
          boxShadow: shadows[4],
        },

        '&:focus': {
          boxShadow: 'none',
        },

        '&:active': {
          boxShadow: shadows[2],
        },
      },
      colorInherit: {
        backgroundColor: palette.primary.xlight,
        color: palette.primary.dark,

        '&:focus, &:active': {
          color: alpha(palette.primary.dark, 0.8),
        },

        '&:hover': {
          backgroundColor: palette.primary.xlight,
        },

        '&:disabled': {
          backgroundColor: palette.primary.xlight,
          color: palette.primary.light,
        },
      },
      primary: {
        backgroundColor: palette.primary.main,
        color: palette.grey[50],

        '&:focus, &:active': {
          color: alpha(palette.grey[50], 0.8),
        },

        '&:hover': {
          backgroundColor: palette.primary.light,
        },

        '&:disabled': {
          backgroundColor: palette.primary.light,
          color: palette.grey[50],
        },
      },
      secondary: {
        backgroundColor: palette.secondary.xlight,
        color: palette.secondary.dark,

        '&:focus, &:active': {
          color: alpha(palette.secondary.dark, 0.8),
        },

        '&:hover': {
          backgroundColor: palette.secondary.xlight,
        },

        '&:disabled': {
          backgroundColor: palette.secondary.xlight,
          color: palette.secondary.light,
        },
      },
      sizeMedium: {
        padding: theme.spacing(1, 1 + 1 / 2),
        height: 40,
        borderRadius: radii.large,
        fontSize: typography.button.fontSize,
        lineHeight: typography.button.lineHeight,
        fontWeight: 600,
      },
    },
    variants: [
      {
        props: { variant: 'square' },
        style: {
          borderRadius: radii.medium,
          fontSize: '10px',
          lineHeight: '10px',
          flexDirection: 'column',
          height: 56,
          width: 56,
          padding: 9,
        },
      },
      {
        props: { color: 'success' },
        style: {
          backgroundColor: palette.success.xlight,
          color: palette.success.main,

          '&:focus, &:active': {
            color: alpha(palette.success.dark, 0.8),
          },

          '&:hover': {
            backgroundColor: palette.success.xlight,
          },

          '&:disabled': {
            backgroundColor: palette.success.xlight,
            color: palette.success.light,
          },
        },
      },
      {
        props: { color: 'info' },
        style: {
          backgroundColor: palette.info.xlight,
          color: palette.info.dark,

          '&:focus, &:active': {
            color: alpha(palette.info.dark, 0.8),
          },

          '&:hover': {
            backgroundColor: palette.info.xlight,
          },

          '&:disabled': {
            backgroundColor: palette.info.xlight,
            color: palette.info.light,
          },
        },
      },
      {
        props: { color: 'warning' },
        style: {
          backgroundColor: palette.warning.xlight,
          color: palette.text.secondary,

          '&:focus, &:active': {
            color: alpha(palette.text.primary, 0.8),
          },

          '&:hover': {
            backgroundColor: palette.warning.xlight,
          },

          '&:disabled': {
            backgroundColor: palette.warning.xlight,
            color: palette.text.disabled,
          },
        },
      },
    ],
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        '&:not(.Mui-selected)': {
          backgroundColor: palette.grey[50],
          borderWidth: 2,

          '&:hover': {
            borderWidth: 2,
            boxShadow: shadows[4],
          },

          '&:active': {
            boxShadow: shadows[2],
          },
        },

        '&.Mui-selected': {
          borderWidth: 0,
          color: palette.grey[50],

          '&:active': {
            color: alpha(palette.grey[50], 0.8),
            boxShadow: shadows[4],
          },

          '&:disabled': {
            color: palette.grey[50],
          },
        },

        '&.MuiToggleButton-info': {
          '&:not(.Mui-selected)': {
            backgroundColor: palette.grey[50],
            ...extraVariants.outlinedInfo,
          },

          '&.Mui-selected': {
            backgroundColor: palette.grey[600],
            ...extraVariants.containedInfo,
          },
        },
      },
      ...extraVariants,
    },
  },
};

export default Button;
